import {
    animate,
    AnimationEvent,
    state,
    style,
    transition,
    trigger,
} from "@angular/animations";
import { DOCUMENT } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";

import { FilterHatchService } from "@hermes/pages/grid-page";
import { GridFacade } from "@hermes/states/grid-filters-state";

@Component({
    selector: "h-grid-filters-mobile-dialog",
    templateUrl: "./grid-filters-mobile-dialog.component.html",
    styleUrls: ["./grid-filters-mobile-dialog.component.scss"],
    animations: [
        trigger("overlayAnimation", [
            state(
                "close",
                style({
                    opacity: 0,
                }),
            ),
            state(
                "open",
                style({
                    opacity: 1,
                }),
            ),
            transition("open <=> close", [animate("250ms")]),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridFiltersMobileDialogComponent
    implements AfterViewInit, OnDestroy, OnChanges
{
    @Input()
    public selectedFilters: number = 0;

    @Output()
    public isMobileFiltersDialogOpen = new EventEmitter<boolean>();

    @ViewChild("closeDialogRef")
    public closeButtonDialog: ElementRef;

    @ViewChild("openDialogRef")
    public openButtonDialog: ElementRef;

    public isGridUpdatedSinceLastOpen: boolean = false;

    public isDialogDisplayed: boolean = false;

    public dialogState: "open" | "close" = "close";

    private subscription: Subscription = new Subscription();

    constructor(
        private renderer: Renderer2,
        private filterHatchService: FilterHatchService,
        private gridFacade: GridFacade,
        private cd: ChangeDetectorRef,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    @HostListener("document:keyup.escape", ["$event"])
    public onKeydownHandler(event: KeyboardEvent): void {
        this.closeDialog(event);
    }

    public ngAfterViewInit(): void {
        this.subscription.add(
            this.filterHatchService.cleared$
                .pipe(filter(() => this.isDialogDisplayed))
                .subscribe(() => {
                    this.closeButtonDialog.nativeElement.focus();
                }),
        );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            this.isDialogDisplayed &&
            changes.selectedFilters.previousValue !== undefined &&
            changes.selectedFilters.previousValue !==
                changes.selectedFilters.currentValue
        ) {
            this.isGridUpdatedSinceLastOpen = true;
            this.cd.markForCheck();
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public openDialog(event: UIEvent): void {
        this.changeDialog(event, true);
        this.renderer.setStyle(this.document.body, "overflow", "hidden");
    }

    public closeDialog(event: UIEvent): void {
        this.changeDialog(event, false);
        this.renderer.removeStyle(this.document.body, "overflow");
    }

    public changeDialog(event: UIEvent, isDialogOpen: boolean): void {
        this.isMobileFiltersDialogOpen.emit(isDialogOpen);
        event.stopPropagation();
        if (isDialogOpen) {
            this.isDialogDisplayed = isDialogOpen;
            this.dialogState = "open";
        } else {
            this.dialogState = "close";
        }
        this.cd.markForCheck();
    }

    public onTotalSelectedFiltersChange(event: number): void {
        this.selectedFilters = event;
        this.cd.markForCheck();
    }

    public onAnimationEnd(event: AnimationEvent): void {
        if (event.fromState !== "void" && event.toState === "close") {
            this.isDialogDisplayed = false;
            this.gridFacade.closeDialog(this.isGridUpdatedSinceLastOpen);
            this.isGridUpdatedSinceLastOpen = false;
            this.cd.markForCheck();
        } else {
            this.gridFacade.openDialog();
        }
    }
}
